/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { strategyApi } from "@/lib/api";
import { useAsyncJob } from "@/lib/useAsyncJob";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Sparkle, Plus, TrashSimple, ChartBar, WarningCircle, Scales,
    UsersThree, ListChecks, Microphone, ArrowUpRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { AIAssistBanner } from "@/components/SourceChip";

const SEVERITY_TONE = { 1: "bg-emerald-100 text-emerald-800", 2: "bg-emerald-100 text-emerald-800", 3: "bg-amber-100 text-amber-800", 4: "bg-amber-100 text-amber-800", 6: "bg-orange-100 text-orange-800", 9: "bg-destructive/15 text-destructive", 12: "bg-destructive/15 text-destructive", 16: "bg-destructive/15 text-destructive" };

export default function StrategyPanel({ matter }) {
    return (
        <div data-testid="strategy-panel" className="space-y-4">
            <AIAssistBanner />
            <Tabs defaultValue="strength">
                <TabsList className="flex-wrap">
                    <TabsTrigger value="strength" data-testid="strategy-tab-strength"><ChartBar size={13} className="mr-1" /> Strength</TabsTrigger>
                    <TabsTrigger value="risks" data-testid="strategy-tab-risks"><WarningCircle size={13} className="mr-1" /> Risks</TabsTrigger>
                    <TabsTrigger value="arguments" data-testid="strategy-tab-arguments"><Scales size={13} className="mr-1" /> Arguments</TabsTrigger>
                    <TabsTrigger value="evidence" data-testid="strategy-tab-evidence"><ListChecks size={13} className="mr-1" /> Evidence</TabsTrigger>
                    <TabsTrigger value="witnesses" data-testid="strategy-tab-witnesses"><UsersThree size={13} className="mr-1" /> Witnesses</TabsTrigger>
                    <TabsTrigger value="appeal" data-testid="strategy-tab-appeal"><ArrowUpRight size={13} className="mr-1" /> Appeal</TabsTrigger>
                </TabsList>
                <TabsContent value="strength" className="pt-4"><CaseStrengthSection matter={matter} /></TabsContent>
                <TabsContent value="risks" className="pt-4"><RisksSection matter={matter} /></TabsContent>
                <TabsContent value="arguments" className="pt-4"><ArgumentsSection matter={matter} /></TabsContent>
                <TabsContent value="evidence" className="pt-4"><EvidenceSection matter={matter} /></TabsContent>
                <TabsContent value="witnesses" className="pt-4"><WitnessesSection matter={matter} /></TabsContent>
                <TabsContent value="appeal" className="pt-4"><AppellateSection matter={matter} /></TabsContent>
            </Tabs>
        </div>
    );
}

// ============== Case Strength ==============

function CaseStrengthSection({ matter }) {
    const [list, setList] = useState([]);
    const [notes, setNotes] = useState("");
    const { job, busy, error, start, reset } = useAsyncJob();

    const load = async () => setList(await strategyApi.listAssessments(matter.id).catch(() => []));
    useEffect(() => { load(); }, [matter.id]);

    const run = async () => {
        const j = await start(() => strategyApi.assess(matter.id, { extra_notes: notes }));
        if (j?.status === "completed") {
            toast.success("Assessment complete");
            setNotes("");
            await load();
        } else if (j?.status === "failed") {
            toast.error(error || j?.error || "Failed");
        }
    };

    const remove = async (id) => {
        try { await strategyApi.removeAssessment(id); await load(); }
        catch { toast.error("Delete failed"); }
    };

    const latest = list[0];

    return (
        <div className="space-y-4">
            <div className="border border-border bg-white rounded-md p-4" data-testid="assess-trigger-card">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Run a Senior Counsel SWOT</div>
                <Textarea
                    rows={2}
                    placeholder="Optional steer — e.g. 'focus on Section 138 limitation defence'"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="assess-extra-notes"
                />
                <div className="flex justify-end pt-2">
                    <Button onClick={run} disabled={busy} data-testid="assess-run-btn">
                        <Sparkle size={14} weight="duotone" /> {busy ? "Analysing…" : "Run AI assessment"}
                    </Button>
                </div>
                {busy && job && <JobProgress job={job} />}
                {error && !busy && <div className="mt-2 text-xs text-destructive">{error} <button onClick={reset} className="underline ml-1">retry</button></div>}
            </div>

            {!latest ? (
                <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">
                    No assessment yet. Click <b>Run AI assessment</b> for a SWOT-style report.
                </div>
            ) : (
                <div className="border border-border bg-white rounded-md p-4 space-y-4" data-testid="assess-card">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Latest assessment</div>
                            <div className="text-xs text-muted-foreground">{new Date(latest.created_at).toLocaleString()}</div>
                        </div>
                        <button onClick={() => remove(latest.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete" data-testid="assess-delete-btn">
                            <TrashSimple size={14} />
                        </button>
                    </div>
                    <AssessmentView data={latest.data || {}} />
                </div>
            )}

            {list.length > 1 && (
                <details className="border border-border rounded-md p-3 bg-white">
                    <summary className="text-xs uppercase tracking-[0.2em] text-muted-foreground cursor-pointer">Previous assessments ({list.length - 1})</summary>
                    <div className="mt-3 space-y-3">
                        {list.slice(1).map((a) => (
                            <div key={a.id} className="border border-border rounded-sm p-3 bg-muted/30" data-testid={`assess-prev-${a.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</div>
                                    <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive"><TrashSimple size={12} /></button>
                                </div>
                                <AssessmentView data={a.data || {}} compact />
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

function AssessmentView({ data, compact = false }) {
    const score = Number(data.likelihood_of_success_pct || 0);
    const tone = score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-destructive";
    return (
        <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
                <div className="text-3xl font-serif">{score}%</div>
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Likelihood of success</div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className={`h-2 ${tone}`} style={{ width: `${Math.min(100, Math.max(0, score))}%` }} />
                    </div>
                </div>
                <div className="text-xs">
                    <div className="text-muted-foreground">Confidence</div>
                    <div className="font-mono uppercase">{data.confidence || "—"}</div>
                </div>
            </div>
            {data.overall_summary && (
                <div className="border-l-2 border-primary/40 pl-3 italic text-muted-foreground">{data.overall_summary}</div>
            )}
            {!compact && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <SwotBlock title="Strengths" tone="emerald" items={data.strengths} render={(s) => (<div><div className="font-medium">{s.point}</div>{s.why && <div className="text-xs text-muted-foreground">{s.why}</div>}</div>)} />
                    <SwotBlock title="Weaknesses" tone="destructive" items={data.weaknesses} render={(s) => (<div><div className="font-medium">{s.point}</div>{s.why && <div className="text-xs text-muted-foreground">{s.why}</div>}</div>)} />
                    <SwotBlock title="Opportunities" tone="amber" items={data.opportunities} />
                    <SwotBlock title="Threats" tone="amber" items={data.threats} />
                </div>
            )}
            {!compact && data.key_legal_questions?.length > 0 && (
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Key legal questions</div>
                    <ul className="list-disc list-inside text-sm space-y-0.5">{data.key_legal_questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
            )}
            {!compact && data.recommended_next_actions?.length > 0 && (
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Recommended next actions</div>
                    <ul className="list-disc list-inside text-sm space-y-0.5">{data.recommended_next_actions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
            )}
        </div>
    );
}

function SwotBlock({ title, tone, items, render }) {
    if (!items || !items.length) return null;
    const ring = {
        emerald: "border-emerald-200 bg-emerald-50/50",
        destructive: "border-destructive/30 bg-destructive/5",
        amber: "border-amber-200 bg-amber-50/50",
    }[tone] || "border-border bg-white";
    return (
        <div className={`border rounded-sm p-3 ${ring}`}>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{title}</div>
            <ul className="space-y-1.5 text-sm">
                {items.map((it, i) => <li key={i}>{render ? render(it) : <span>{String(it)}</span>}</li>)}
            </ul>
        </div>
    );
}

// ============== Risks ==============

function RisksSection({ matter }) {
    const [risks, setRisks] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setRisks(await strategyApi.listRisks(matter.id).catch(() => []));
    useEffect(() => { load(); }, [matter.id]);

    const sevPill = (sev) => {
        const cls = SEVERITY_TONE[sev] || "bg-muted text-muted-foreground";
        return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>Severity {sev}</span>;
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">{risks.length} risk(s) on this matter</div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" data-testid="risk-add-btn"><Plus size={13} /> Add risk</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New risk</DialogTitle>
                            <DialogDescription>Capture a litigation risk with likelihood × impact = severity score.</DialogDescription>
                        </DialogHeader>
                        <RiskForm matter={matter} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!risks.length ? (
                <EmptyState text="No risks logged yet. Capture witness no-show, opposing counsel tactics, limitation, and procedural risks here." />
            ) : (
                <div className="space-y-2">
                    {risks.map((r) => (
                        <RiskRow key={r.id} risk={r} sevPill={sevPill} onChange={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function RiskRow({ risk, sevPill, onChange }) {
    const [editing, setEditing] = useState(false);
    const [v, setV] = useState(risk);
    useEffect(() => setV(risk), [risk]);

    const save = async () => {
        try {
            await strategyApi.updateRisk(risk.id, {
                title: v.title, description: v.description, likelihood: v.likelihood,
                impact: v.impact, mitigation: v.mitigation, owner: v.owner, due_date: v.due_date || null, status: v.status,
            });
            toast.success("Risk updated");
            setEditing(false);
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const remove = async () => {
        try { await strategyApi.removeRisk(risk.id); onChange?.(); }
        catch { toast.error("Delete failed"); }
    };

    if (!editing) {
        return (
            <div className="border border-border bg-white rounded-md p-3" data-testid={`risk-${risk.id}`}>
                <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{risk.title}</span>
                            {sevPill(risk.severity)}
                            <span className="chip chip-doc text-xs">{risk.status}</span>
                            <span className="text-xs text-muted-foreground">L:{risk.likelihood} · I:{risk.impact}</span>
                        </div>
                        {risk.description && <div className="text-sm text-muted-foreground mt-1">{risk.description}</div>}
                        {risk.mitigation && <div className="text-xs mt-1"><b>Mitigation:</b> {risk.mitigation}</div>}
                        <div className="text-xs text-muted-foreground mt-1">
                            {risk.owner && <span>Owner: {risk.owner} · </span>}
                            {risk.due_date && <span>Due: {risk.due_date}</span>}
                        </div>
                    </div>
                    <div className="flex flex-col gap-1">
                        <button onClick={() => setEditing(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid={`risk-edit-${risk.id}`}>Edit</button>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <button className="text-muted-foreground hover:text-destructive" data-testid={`risk-delete-${risk.id}`}><TrashSimple size={13} /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this risk?</AlertDialogTitle>
                                    <AlertDialogDescription>This will remove "{risk.title}" from the risk register.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="border-2 border-primary/40 bg-white rounded-md p-3 space-y-2" data-testid={`risk-editing-${risk.id}`}>
            <Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} />
            <Textarea rows={2} value={v.description || ""} onChange={(e) => setV({ ...v, description: e.target.value })} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <SelectFor label="Likelihood" value={v.likelihood} setValue={(x) => setV({ ...v, likelihood: x })} options={["low", "medium", "high", "critical"]} />
                <SelectFor label="Impact" value={v.impact} setValue={(x) => setV({ ...v, impact: x })} options={["low", "medium", "high", "critical"]} />
                <SelectFor label="Status" value={v.status} setValue={(x) => setV({ ...v, status: x })} options={["open", "mitigating", "accepted", "closed"]} />
                <Input type="date" value={v.due_date || ""} onChange={(e) => setV({ ...v, due_date: e.target.value })} />
            </div>
            <Input placeholder="Mitigation plan" value={v.mitigation || ""} onChange={(e) => setV({ ...v, mitigation: e.target.value })} />
            <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" onClick={save}>Save</Button>
            </div>
        </div>
    );
}

function RiskForm({ matter, onDone }) {
    const [v, setV] = useState({ title: "", description: "", likelihood: "medium", impact: "medium", mitigation: "", owner: "", due_date: "", status: "open" });
    const submit = async () => {
        if (!v.title) { toast.error("Title is required"); return; }
        try {
            await strategyApi.createRisk({ ...v, matter_id: matter.id, due_date: v.due_date || null });
            toast.success("Risk added");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="risk-form">
            <Input placeholder="Risk title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="risk-title" />
            <Textarea rows={2} placeholder="Description" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="risk-desc" />
            <div className="grid grid-cols-2 gap-2 text-sm">
                <SelectFor label="Likelihood" value={v.likelihood} setValue={(x) => setV({ ...v, likelihood: x })} options={["low", "medium", "high", "critical"]} testid="risk-likelihood" />
                <SelectFor label="Impact" value={v.impact} setValue={(x) => setV({ ...v, impact: x })} options={["low", "medium", "high", "critical"]} testid="risk-impact" />
            </div>
            <Input placeholder="Mitigation plan (optional)" value={v.mitigation} onChange={(e) => setV({ ...v, mitigation: e.target.value })} data-testid="risk-mitigation" />
            <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Owner" value={v.owner} onChange={(e) => setV({ ...v, owner: e.target.value })} />
                <Input type="date" value={v.due_date} onChange={(e) => setV({ ...v, due_date: e.target.value })} />
            </div>
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="risk-submit">Add</Button></div>
        </div>
    );
}

// ============== Arguments ==============

function ArgumentsSection({ matter }) {
    const [args, setArgs] = useState([]);
    const [open, setOpen] = useState(false);
    const [suggested, setSuggested] = useState(null);
    const { job: suggestJob, busy: suggBusy, start: startSuggest, error: suggError, reset: suggReset } = useAsyncJob();
    const [posFilter, setPosFilter] = useState("all");

    const load = async () => setArgs(await strategyApi.listArguments(matter.id).catch(() => []));
    useEffect(() => { load(); }, [matter.id]);

    const filtered = useMemo(() => posFilter === "all" ? args : args.filter((a) => a.position === posFilter), [args, posFilter]);
    const remove = async (id) => { try { await strategyApi.removeArgument(id); await load(); } catch { toast.error("Failed"); } };

    const suggest = async (position) => {
        const j = await startSuggest(() => strategyApi.suggestArguments({ matter_id: matter.id, position }));
        if (j?.status === "completed") {
            setSuggested({ position: j.result?.position || position, items: j.result?.suggestions || [] });
        } else if (j?.status === "failed") {
            toast.error(suggError || j.error || "AI failed");
        }
    };

    const adopt = async (s, position) => {
        try {
            await strategyApi.createArgument({
                matter_id: matter.id, title: s.title, position, legal_basis: s.legal_basis,
                facts_supporting: s.facts_supporting || [], counter_arguments: s.counter_arguments || [],
                rebuttals: s.rebuttals || [], strength: s.strength || "moderate",
            });
            toast.success("Argument added");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center justify-between">
                <div className="flex gap-2 items-center">
                    <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Filter</span>
                    <Select value={posFilter} onValueChange={setPosFilter}>
                        <SelectTrigger className="w-[140px]" data-testid="args-pos-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All positions</SelectItem>
                            <SelectItem value="for">For our side</SelectItem>
                            <SelectItem value="against">Anticipated against</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => suggest("for")} disabled={suggBusy} data-testid="suggest-for-btn"><Sparkle size={13} /> Suggest (for us)</Button>
                    <Button size="sm" variant="outline" onClick={() => suggest("against")} disabled={suggBusy} data-testid="suggest-against-btn"><Sparkle size={13} /> Suggest (against us)</Button>                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><Button size="sm" data-testid="arg-add-btn"><Plus size={13} /> Add manually</Button></DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>New argument</DialogTitle>
                                <DialogDescription>Capture a discrete legal argument with its supporting facts and anticipated counters.</DialogDescription>
                            </DialogHeader>
                            <ArgumentForm matter={matter} onDone={() => { setOpen(false); load(); }} />
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {suggBusy && suggestJob && (
                <div className="border border-border bg-white rounded-md p-3" data-testid="suggest-progress">
                    <div className="text-xs text-muted-foreground">Suggesting arguments…</div>
                    <JobProgress job={suggestJob} />
                </div>
            )}
            {suggError && !suggBusy && <div className="text-xs text-destructive">{suggError} <button onClick={suggReset} className="underline ml-1">retry</button></div>}

            {suggested && (
                <div className="border-2 border-primary/30 bg-primary/5 rounded-md p-4 space-y-3" data-testid="suggested-args">
                    <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI suggestions — {suggested.position}</div>
                        <button onClick={() => setSuggested(null)} className="text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
                    </div>
                    <div className="space-y-2">
                        {suggested.items.map((s, i) => (
                            <div key={i} className="border border-border bg-white rounded-sm p-3" data-testid={`suggested-arg-${i}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="chip chip-ai">{s.strength}</span>
                                    <span className="font-medium">{s.title}</span>
                                </div>
                                {s.legal_basis && <div className="text-xs"><b>Basis:</b> {s.legal_basis}</div>}
                                {s.facts_supporting?.length > 0 && <div className="text-xs mt-1"><b>Facts:</b> {s.facts_supporting.join("; ")}</div>}
                                {s.counter_arguments?.length > 0 && <div className="text-xs mt-1"><b>Counters:</b> {s.counter_arguments.join("; ")}</div>}
                                <div className="flex justify-end pt-2">
                                    <Button size="sm" variant="outline" onClick={() => adopt(s, suggested.position)} data-testid={`adopt-arg-${i}`}>Add to register</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!filtered.length ? <EmptyState text="No arguments yet. Use AI suggestions or add manually." /> : (
                <div className="space-y-2">
                    {filtered.map((a) => <ArgumentCard key={a.id} arg={a} onChange={load} onRemove={() => remove(a.id)} />)}
                </div>
            )}
        </div>
    );
}

function ArgumentCard({ arg, onChange, onRemove }) {
    const strengthChip = { weak: "chip-doc", moderate: "chip-ai", strong: "chip-ai" }[arg.strength] || "chip-doc";
    return (
        <div className="border border-border bg-white rounded-md p-3" data-testid={`arg-${arg.id}`}>
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{arg.title}</span>
                        <span className={`chip ${strengthChip}`}>{arg.strength}</span>
                        <span className="chip chip-doc">{arg.position === "for" ? "for us" : "against us"}</span>
                    </div>
                    {arg.legal_basis && <div className="text-xs mt-1"><b>Basis:</b> {arg.legal_basis}</div>}
                    {arg.facts_supporting?.length > 0 && <div className="text-xs mt-1"><b>Facts:</b> <ul className="list-disc list-inside ml-1">{arg.facts_supporting.map((f, i) => <li key={i}>{f}</li>)}</ul></div>}
                    {arg.counter_arguments?.length > 0 && <div className="text-xs mt-1"><b>Counters:</b> {arg.counter_arguments.join("; ")}</div>}
                    {arg.rebuttals?.length > 0 && <div className="text-xs mt-1"><b>Rebuttals:</b> {arg.rebuttals.join("; ")}</div>}
                </div>
                <button onClick={onRemove} className="text-muted-foreground hover:text-destructive" aria-label="Delete" data-testid={`arg-delete-${arg.id}`}><TrashSimple size={14} /></button>
            </div>
        </div>
    );
}

function ArgumentForm({ matter, onDone }) {
    const [v, setV] = useState({ title: "", position: "for", legal_basis: "", facts_supporting: "", counter_arguments: "", strength: "moderate" });
    const submit = async () => {
        if (!v.title) { toast.error("Title required"); return; }
        try {
            await strategyApi.createArgument({
                matter_id: matter.id, title: v.title, position: v.position,
                legal_basis: v.legal_basis, strength: v.strength,
                facts_supporting: v.facts_supporting.split(/\n+/).map((x) => x.trim()).filter(Boolean),
                counter_arguments: v.counter_arguments.split(/\n+/).map((x) => x.trim()).filter(Boolean),
            });
            toast.success("Argument added");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="argument-form">
            <Input placeholder="Argument title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
                <SelectFor label="Position" value={v.position} setValue={(x) => setV({ ...v, position: x })} options={["for", "against"]} />
                <SelectFor label="Strength" value={v.strength} setValue={(x) => setV({ ...v, strength: x })} options={["weak", "moderate", "strong"]} />
            </div>
            <Input placeholder="Legal basis (Section / Article / Case)" value={v.legal_basis} onChange={(e) => setV({ ...v, legal_basis: e.target.value })} />
            <Textarea rows={3} placeholder="Supporting facts — one per line" value={v.facts_supporting} onChange={(e) => setV({ ...v, facts_supporting: e.target.value })} />
            <Textarea rows={2} placeholder="Anticipated counters — one per line" value={v.counter_arguments} onChange={(e) => setV({ ...v, counter_arguments: e.target.value })} />
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="arg-submit">Add</Button></div>
        </div>
    );
}

// ============== Evidence ==============

function EvidenceSection({ matter }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setItems(await strategyApi.listEvidence(matter.id).catch(() => []));
    useEffect(() => { load(); }, [matter.id]);
    const remove = async (id) => { try { await strategyApi.removeEvidence(id); await load(); } catch { toast.error("Failed"); } };
    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">{items.length} evidence item(s)</div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button size="sm" data-testid="evidence-add-btn"><Plus size={13} /> Plan evidence</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Plan evidence</DialogTitle>
                            <DialogDescription>Track what you intend to put on record, with exhibit mark and status.</DialogDescription>
                        </DialogHeader>
                        <EvidenceForm matter={matter} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!items.length ? <EmptyState text="No evidence planned yet. Build the exhibit list before filing." /> : (
                <div className="border border-border bg-white rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-2">Mark</th>
                                <th className="text-left p-2">Title</th>
                                <th className="text-left p-2 hidden md:table-cell">Type</th>
                                <th className="text-left p-2 hidden md:table-cell">Purpose</th>
                                <th className="text-left p-2">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((e) => (
                                <tr key={e.id} className="border-t border-border" data-testid={`evidence-${e.id}`}>
                                    <td className="p-2 font-mono text-xs">{e.exhibit_mark || "—"}</td>
                                    <td className="p-2">{e.title}</td>
                                    <td className="p-2 hidden md:table-cell"><span className="chip chip-doc text-xs">{e.type}</span></td>
                                    <td className="p-2 hidden md:table-cell text-xs text-muted-foreground">{e.purpose}</td>
                                    <td className="p-2"><span className="chip chip-ai text-xs">{e.status}</span></td>
                                    <td className="p-2"><button onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive" data-testid={`evidence-delete-${e.id}`}><TrashSimple size={13} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function EvidenceForm({ matter, onDone }) {
    const [v, setV] = useState({ title: "", type: "document", purpose: "", source: "", status: "planned", exhibit_mark: "" });
    const submit = async () => {
        if (!v.title) { toast.error("Title required"); return; }
        try {
            await strategyApi.createEvidence({ ...v, matter_id: matter.id });
            toast.success("Evidence added");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="evidence-form">
            <Input placeholder="Evidence title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
                <SelectFor label="Type" value={v.type} setValue={(x) => setV({ ...v, type: x })} options={["document", "witness", "expert", "digital", "physical", "judicial_notice"]} />
                <SelectFor label="Status" value={v.status} setValue={(x) => setV({ ...v, status: x })} options={["planned", "collected", "exhibited", "objected", "rejected"]} />
            </div>
            <Input placeholder="Exhibit mark (P-1, R-3, …)" value={v.exhibit_mark} onChange={(e) => setV({ ...v, exhibit_mark: e.target.value })} />
            <Textarea rows={2} placeholder="What fact does this prove?" value={v.purpose} onChange={(e) => setV({ ...v, purpose: e.target.value })} />
            <Input placeholder="Source / custodian" value={v.source} onChange={(e) => setV({ ...v, source: e.target.value })} />
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="evidence-submit">Add</Button></div>
        </div>
    );
}

// ============== Witnesses ==============

function WitnessesSection({ matter }) {
    const [wits, setWits] = useState([]);
    const [open, setOpen] = useState(false);
    const load = async () => setWits(await strategyApi.listWitnesses(matter.id).catch(() => []));
    useEffect(() => { load(); }, [matter.id]);
    const remove = async (id) => { try { await strategyApi.removeWitness(id); await load(); } catch { toast.error("Failed"); } };
    const prep = async (id) => {
        toast.message("Generating examination plan…");
        try {
            await strategyApi.witnessPrep(id);
            toast.success("Prep pack ready");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">{wits.length} witness(es)</div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button size="sm" data-testid="witness-add-btn"><Plus size={13} /> Add witness</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New witness</DialogTitle>
                            <DialogDescription>Once added, use "Generate prep pack" to draft anticipated questions.</DialogDescription>
                        </DialogHeader>
                        <WitnessForm matter={matter} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>
            {!wits.length ? <EmptyState text="No witnesses recorded. Add own / opposing / expert witnesses to prepare examination plans." /> : (
                <div className="space-y-2">
                    {wits.map((w) => <WitnessCard key={w.id} w={w} onPrep={() => prep(w.id)} onRemove={() => remove(w.id)} isPreppingThis={prepBusy && prepFor === w.id} />)}
                </div>
            )}
        </div>
    );
}

function WitnessCard({ w, onPrep, onRemove, isPreppingThis }) {
    const [showQs, setShowQs] = useState(false);
    return (
        <div className="border border-border bg-white rounded-md p-3" data-testid={`witness-${w.id}`}>
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{w.name}</span>
                        <span className="chip chip-doc">{w.role}</span>
                        <span className="chip chip-ai">{w.examination_type}</span>
                        <span className="chip">{w.status}</span>
                    </div>
                    {w.key_facts_to_establish?.length > 0 && (
                        <div className="text-xs mt-1"><b>Facts to establish:</b> {w.key_facts_to_establish.join("; ")}</div>
                    )}
                    {w.anticipated_questions?.length > 0 && (
                        <div className="mt-2">
                            <button className="text-xs underline text-muted-foreground hover:text-foreground" onClick={() => setShowQs((s) => !s)} data-testid={`witness-show-qs-${w.id}`}>
                                {showQs ? "Hide" : "Show"} {w.anticipated_questions.length} anticipated questions
                            </button>
                            {showQs && (
                                <ol className="mt-2 list-decimal list-inside text-sm space-y-1 border-l-2 border-border pl-3">
                                    {w.anticipated_questions.map((q, i) => <li key={i}>{q}</li>)}
                                </ol>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-1 items-end">
                    <Button size="sm" variant="outline" onClick={onPrep} disabled={isPreppingThis} data-testid={`witness-prep-${w.id}`}>
                        <Microphone size={12} /> {isPreppingThis ? "Drafting…" : "Prep pack"}
                    </Button>
                    <button onClick={onRemove} className="text-muted-foreground hover:text-destructive" data-testid={`witness-delete-${w.id}`}><TrashSimple size={13} /></button>
                </div>
            </div>
        </div>
    );
}

function WitnessForm({ matter, onDone }) {
    const [v, setV] = useState({ name: "", role: "own", examination_type: "chief", key_facts_to_establish: "", prep_notes: "", status: "identified" });
    const submit = async () => {
        if (!v.name) { toast.error("Name required"); return; }
        try {
            await strategyApi.createWitness({
                matter_id: matter.id, name: v.name, role: v.role, examination_type: v.examination_type,
                status: v.status, prep_notes: v.prep_notes,
                key_facts_to_establish: v.key_facts_to_establish.split(/\n+/).map((x) => x.trim()).filter(Boolean),
            });
            toast.success("Witness added");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="witness-form">
            <Input placeholder="Witness name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
                <SelectFor label="Role" value={v.role} setValue={(x) => setV({ ...v, role: x })} options={["own", "opposing", "expert", "panchnama", "investigating_officer", "other"]} />
                <SelectFor label="Examination" value={v.examination_type} setValue={(x) => setV({ ...v, examination_type: x })} options={["chief", "cross", "re_examination", "tendered"]} />
            </div>
            <Textarea rows={3} placeholder="Key facts to establish — one per line" value={v.key_facts_to_establish} onChange={(e) => setV({ ...v, key_facts_to_establish: e.target.value })} />
            <Textarea rows={2} placeholder="Prep notes" value={v.prep_notes} onChange={(e) => setV({ ...v, prep_notes: e.target.value })} />
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="witness-submit">Add</Button></div>
        </div>
    );
}

// ============== Appellate ==============

function AppellateSection({ matter }) {
    const [grounds, setGrounds] = useState([]);
    const [open, setOpen] = useState(false);
    const { job: draftJob, busy: draftBusy, start: startDraft, error: draftError, reset: draftReset } = useAsyncJob();
    const [suggested, setSuggested] = useState(null);
    const [orderSummary, setOrderSummary] = useState("");
    const [appCourt, setAppCourt] = useState("Delhi High Court");

    const load = async () => setGrounds(await strategyApi.listAppellate(matter.id).catch(() => []));
    useEffect(() => { load(); }, [matter.id]);
    const remove = async (id) => { try { await strategyApi.removeAppellate(id); await load(); } catch { toast.error("Failed"); } };

    const draft = async () => {
        const j = await startDraft(() => strategyApi.draftGrounds({ matter_id: matter.id, impugned_order_summary: orderSummary, appellate_court: appCourt }));
        if (j?.status === "completed") {
            setSuggested(j.result);
        } else if (j?.status === "failed") {
            toast.error(draftError || j.error || "AI failed");
        }
    };

    const adopt = async (g) => {
        try {
            await strategyApi.createAppellate({
                matter_id: matter.id, ground_no: g.ground_no || (grounds.length + 1),
                statement: g.statement, particulars: g.particulars || "",
                supporting_precedents: g.supporting_precedents || [],
                prayer: suggested?.prayer || "",
            });
            toast.success("Ground added");
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-3">
            <div className="border border-border bg-white rounded-md p-3" data-testid="appellate-draft-card">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">AI: draft grounds of appeal</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input placeholder="Impugned order summary (optional)" value={orderSummary} onChange={(e) => setOrderSummary(e.target.value)} />
                    <Input placeholder="Appellate court" value={appCourt} onChange={(e) => setAppCourt(e.target.value)} />
                </div>
                <div className="flex justify-between items-center mt-2">
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="ground-add-btn"><Plus size={13} /> Add manually</Button></DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>New ground of appeal</DialogTitle>
                                <DialogDescription>Numbered grounds with statement, particulars, and supporting precedents.</DialogDescription>
                            </DialogHeader>
                            <GroundForm matter={matter} nextNo={grounds.length + 1} onDone={() => { setOpen(false); load(); }} />
                        </DialogContent>
                    </Dialog>
                    <Button size="sm" onClick={draft} disabled={draftBusy} data-testid="ground-draft-btn">
                        <Sparkle size={13} /> {draftBusy ? "Drafting…" : "Draft grounds with AI"}
                    </Button>
                </div>
            </div>

            {draftBusy && draftJob && (
                <div className="border border-border bg-white rounded-md p-3" data-testid="draft-grounds-progress">
                    <div className="text-xs text-muted-foreground">Drafting grounds of appeal…</div>
                    <JobProgress job={draftJob} />
                </div>
            )}
            {draftError && !draftBusy && <div className="text-xs text-destructive">{draftError} <button onClick={draftReset} className="underline ml-1">retry</button></div>}

            {suggested && (
                <div className="border-2 border-primary/30 bg-primary/5 rounded-md p-3 space-y-2" data-testid="suggested-grounds">
                    <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">AI-drafted grounds</div>
                        <button onClick={() => setSuggested(null)} className="text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
                    </div>
                    {(suggested.grounds || []).map((g, i) => (
                        <div key={i} className="border border-border bg-white rounded-sm p-3" data-testid={`suggested-ground-${i}`}>
                            <div className="text-xs font-mono uppercase text-muted-foreground">Ground {g.ground_no}</div>
                            <div className="font-medium text-sm">{g.statement}</div>
                            {g.particulars && <div className="text-xs mt-1">{g.particulars}</div>}
                            {g.supporting_precedents?.length > 0 && <div className="text-xs mt-1 text-muted-foreground italic">Precedents: {g.supporting_precedents.join("; ")}</div>}
                            <div className="flex justify-end pt-1"><Button size="sm" variant="outline" onClick={() => adopt(g)} data-testid={`adopt-ground-${i}`}>Add to register</Button></div>
                        </div>
                    ))}
                    {suggested.prayer && <div className="border-l-2 border-primary/40 pl-3 italic text-xs">Prayer: {suggested.prayer}</div>}
                </div>
            )}

            {!grounds.length ? <EmptyState text="No grounds yet. Use AI or add manually." /> : (
                <div className="space-y-2">
                    {grounds.map((g) => (
                        <div key={g.id} className="border border-border bg-white rounded-md p-3" data-testid={`ground-${g.id}`}>
                            <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs font-mono uppercase text-muted-foreground">Ground {g.ground_no}</div>
                                    <div className="font-medium">{g.statement}</div>
                                    {g.particulars && <div className="text-xs mt-1">{g.particulars}</div>}
                                    {g.supporting_precedents?.length > 0 && <div className="text-xs mt-1 text-muted-foreground italic">Precedents: {g.supporting_precedents.join("; ")}</div>}
                                </div>
                                <button onClick={() => remove(g.id)} className="text-muted-foreground hover:text-destructive" data-testid={`ground-delete-${g.id}`}><TrashSimple size={13} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function GroundForm({ matter, nextNo, onDone }) {
    const [v, setV] = useState({ ground_no: nextNo, statement: "", particulars: "", supporting_precedents: "", prayer: "" });
    const submit = async () => {
        if (!v.statement) { toast.error("Statement required"); return; }
        try {
            await strategyApi.createAppellate({
                matter_id: matter.id, ground_no: parseInt(v.ground_no, 10) || nextNo,
                statement: v.statement, particulars: v.particulars, prayer: v.prayer,
                supporting_precedents: v.supporting_precedents.split(/\n+/).map((x) => x.trim()).filter(Boolean),
            });
            toast.success("Ground added");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="ground-form">
            <Input type="number" placeholder="Ground no." value={v.ground_no} onChange={(e) => setV({ ...v, ground_no: e.target.value })} />
            <Textarea rows={2} placeholder="For that the learned Court below has erred…" value={v.statement} onChange={(e) => setV({ ...v, statement: e.target.value })} />
            <Textarea rows={2} placeholder="Particulars" value={v.particulars} onChange={(e) => setV({ ...v, particulars: e.target.value })} />
            <Textarea rows={2} placeholder="Supporting precedents — one per line" value={v.supporting_precedents} onChange={(e) => setV({ ...v, supporting_precedents: e.target.value })} />
            <Input placeholder="Prayer" value={v.prayer} onChange={(e) => setV({ ...v, prayer: e.target.value })} />
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="ground-submit">Add</Button></div>
        </div>
    );
}

// ============== Shared helpers ==============

function SelectFor({ label, value, setValue, options, testid }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
            <Select value={value} onValueChange={setValue}>
                <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
                <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
        </div>
    );
}

function EmptyState({ text }) {
    return (
        <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">{text}</div>
    );
}

export function JobProgress({ job }) {
    if (!job?.stages?.length) return null;
    return (
        <div className="mt-3 border-t border-border pt-2 space-y-1" data-testid="job-progress">
            {job.stages.map((s) => {
                const dot = {
                    queued: "bg-muted-foreground/30",
                    processing: "bg-primary animate-pulse",
                    completed: "bg-emerald-500",
                    failed: "bg-destructive",
                }[s.status] || "bg-muted-foreground/30";
                return (
                    <div key={s.key} className="flex items-center gap-2 text-xs">
                        <span className={`w-2 h-2 rounded-full ${dot}`} />
                        <span className="font-medium">{s.label}</span>
                        {s.reasoning && <span className="text-muted-foreground italic truncate">— {s.reasoning}</span>}
                    </div>
                );
            })}
        </div>
    );
}
